import PropTypes from "prop-types";
import React, { useState, useContext, createContext, useEffect, useMemo, useCallback } from "react";
import CONFIG from "../config/config";
import { OTP_SEND_ERROR_MESSAGE, OTP_VERIFY_ERROR_MESSAGE } from "../utils/errorMessages";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem("portalToken");
    const savedUser = localStorage.getItem("portalUser");
    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.removeItem("portalToken");
        localStorage.removeItem("portalUser");
      }
    }
    setInitializing(false);
  }, []);

  // Step 1 of OTP login — email a one-time code. Mirrors botriq-admin-fe's
  // AuthProvider.sendOtp so both portals behave identically.
  //
  // Accepts an optional AbortSignal so a caller can cancel a send it has
  // abandoned. An aborted send resolves as { success: false, aborted: true }
  // rather than throwing or reporting a generic failure, so the caller can tell
  // "the user walked away" apart from "the send genuinely failed" and skip both
  // the error message and the advance-to-code-step that a success would trigger.
  const sendOtp = async (email, { signal } = {}) => {
    setLoading(true);
    try {
      const response = await fetch(`${CONFIG.BASE_URL}/api/v1/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
        signal,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        // 404 = otp.service.js's processOtpRequest found no user for this
        // email — a distinct, actionable failure ("this email isn't
        // registered") from every other case ("try again later"), so it gets
        // its own message instead of the generic OTP-send fallback below.
        if (response.status === 404) {
          return {
            success: false,
            error: "This email is not registered. Check the address, or register for an account.",
          };
        }
        throw new Error(data.message || "Failed to send OTP");
      }
      return { success: true };
    } catch (err) {
      // A cancelled request is not a failure to report — the caller asked for it.
      if (err?.name === "AbortError") return { success: false, aborted: true };
      return { success: false, error: OTP_SEND_ERROR_MESSAGE };
    } finally {
      setLoading(false);
    }
  };

  // Step 2 of OTP login — verify the code and establish the session.
  // credentials:'include' is required for the httpOnly refresh cookie the v1
  // endpoint sets.
  const verifyOtp = async (email, otp) => {
    setLoading(true);
    try {
      const response = await fetch(`${CONFIG.BASE_URL}/api/v1/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, otp }),
      });
      if (!response.ok) {
        throw new Error("OTP verification failed");
      }
      const data = await response.json();
      localStorage.setItem("portalToken", data.token);
      localStorage.setItem("portalUser", JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
      return { success: true };
    } catch {
      return { success: false, error: OTP_VERIFY_ERROR_MESSAGE };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const savedToken = localStorage.getItem("portalToken");
      if (savedToken) {
        await fetch(`${CONFIG.BASE_URL}/api/v1/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${savedToken}` },
          credentials: "include",
        });
      }
    } catch {
      /* silent */
    }
    localStorage.removeItem("portalToken");
    localStorage.removeItem("portalUser");
    setToken(null);
    setUser(null);
  };

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const updated = { ...prev, ...updates };
      localStorage.setItem("portalUser", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      sendOtp,
      verifyOtp,
      logout,
      updateUser,
      loading,
      initializing,
      isAuthenticated: Boolean(token && user),
    }),
    // sendOtp/verifyOtp/logout are stable in behavior though not useCallback-wrapped;
    // matches this file's existing convention.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, token, loading, initializing, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = { children: PropTypes.node.isRequired };
